import React from 'react';
import { FileJson, Download, Code, MapPin, CheckCircle, ExternalLink, MessageCircle } from 'lucide-react';
import brandConfig from '../../config/brandConfig';

const ConvertirGeoJSON = () => {
  return (
    <div className="bg-white dark:bg-gray-900 transition-colors">
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <FileJson className="w-12 h-12 text-blue-600 dark:text-blue-400 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              Cómo Convertir a GeoJSON
            </h1>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Guía completa para convertir tus archivos de datos geoespaciales al formato GeoJSON requerido por {brandConfig.name}
          </p>
        </div>

        {/* What is GeoJSON */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 mb-12 text-white">
          <div className="flex items-center mb-4">
            <MapPin className="w-6 h-6 mr-3" />
            <h2 className="text-2xl font-bold">¿Qué es GeoJSON?</h2>
          </div>
          <p className="text-lg mb-4">
            GeoJSON es un formato estándar abierto diseñado para representar características geográficas simples
            junto con sus atributos no espaciales. Es ampliamente utilizado en aplicaciones web de mapas y es
            compatible con la mayoría de herramientas GIS.
          </p>
          <div className="bg-white/10 rounded-lg p-4 mt-4">
            <p className="text-sm">
              <strong>Formato requerido:</strong> Tu archivo debe tener la extensión <code className="bg-white/20 px-2 py-1 rounded">.geojson</code> o <code className="bg-white/20 px-2 py-1 rounded">.json</code>
            </p>
          </div>
        </div>

        {/* Conversion Methods */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Métodos de Conversión
          </h2>

          <div className="space-y-8">
            {/* QGIS */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">QGIS (Recomendado)</h3>
                  <p className="text-gray-600 dark:text-gray-400">Software GIS gratuito y de código abierto</p>
                </div>
              </div>
              <div className="ml-16 space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Pasos:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Abre QGIS y carga tu archivo (Shapefile, KML, GPX, etc.)</li>
                    <li>Haz clic derecho en la capa en el panel de capas</li>
                    <li>Selecciona <strong>"Exportar" → "Guardar entidades como..."</strong></li>
                    <li>En el formato, selecciona <strong>"GeoJSON"</strong></li>
                    <li>Elige la ubicación y nombre del archivo</li>
                    <li>Haz clic en <strong>"OK"</strong></li>
                  </ol>
                </div>
                <div className="flex items-center space-x-2">
                  <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <a
                    href="https://qgis.org/es/site/forusers/download.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium flex items-center"
                  >
                    Descargar QGIS
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>
            </div>

            {/* Online Converters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-4">
                  <Code className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Convertidores Online</h3>
                  <p className="text-gray-600 dark:text-gray-400">Herramientas web gratuitas para conversión rápida</p>
                </div>
              </div>
              <div className="ml-16 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">MyGeodata Converter</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Convierte entre múltiples formatos geoespaciales
                    </p>
                    <a
                      href="https://mygeodata.cloud/converter/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm font-medium flex items-center"
                    >
                      Ir al convertidor
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Ogre</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Conversor de datos geoespaciales de MapBox
                    </p>
                    <a
                      href="https://ogre.adc4gis.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm font-medium flex items-center"
                    >
                      Ir al convertidor
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">CloudConvert</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Conversor de archivos en la nube
                    </p>
                    <a
                      href="https://cloudconvert.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm font-medium flex items-center"
                    >
                      Ir al convertidor
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Zamzar</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Conversor de archivos online
                    </p>
                    <a
                      href="https://www.zamzar.com/convert/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm font-medium flex items-center"
                    >
                      Ir al convertidor
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Python Script */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mr-4">
                  <Code className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Script Python</h3>
                  <p className="text-gray-600 dark:text-gray-400">Para usuarios con conocimientos técnicos</p>
                </div>
              </div>
              <div className="ml-16 space-y-4">
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-green-400 text-sm">
{`# Instalar librería: pip install geopandas

import geopandas as gpd

# Leer archivo (Shapefile, KML, etc.)
gdf = gpd.read_file('tu_archivo.shp')

# Guardar como GeoJSON
gdf.to_file('salida.geojson', driver='GeoJSON')`}
                  </pre>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Requiere Python y la librería <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">geopandas</code>
                </p>
              </div>
            </div>

            {/* Excel/CSV to GeoJSON */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mr-4">
                  <FileJson className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Desde Excel/CSV</h3>
                  <p className="text-gray-600 dark:text-gray-400">Si tienes coordenadas en una hoja de cálculo</p>
                </div>
              </div>
              <div className="ml-16 space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Requisitos:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li>Tu archivo debe tener columnas con coordenadas (latitud y longitud)</li>
                    <li>O columnas con direcciones que puedan geocodificarse</li>
                    <li>Usa herramientas como <strong>Google My Maps</strong> o <strong>QGIS</strong> para convertir</li>
                  </ul>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Nota:</strong> Si tienes muchas filas, considera usar un script Python con
                    <code className="bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded mx-1">pandas</code> y
                    <code className="bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded mx-1">geopandas</code>
                  </p>
                </div>
              </div>
            </div>

            {/* AutoCAD/CAD to GeoJSON */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-blue-300 dark:border-blue-600 p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mr-4">
                  <MapPin className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Desde AutoCAD (DWG/DXF)</h3>
                  <p className="text-gray-600 dark:text-gray-400">Método común en Perú para datos de lotes y planos</p>
                </div>
              </div>
              <div className="ml-16 space-y-6">
                {/* Método 1: QGIS */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Método 1: Usando QGIS (Recomendado)</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Instala QGIS (gratuito y de código abierto)</li>
                      <li>Abre QGIS y ve a <strong>"Capa" → "Agregar Capa" → "Agregar Capa de Vector"</strong></li>
                      <li>En el tipo de fuente, selecciona <strong>"Archivo"</strong></li>
                      <li>Navega y selecciona tu archivo <strong>.dwg</strong> o <strong>.dxf</strong></li>
                      <li>QGIS puede pedirte que selecciones qué capas del DWG quieres importar</li>
                      <li>Una vez cargado, haz clic derecho en la capa en el panel de capas</li>
                      <li>Selecciona <strong>"Exportar" → "Guardar entidades como..."</strong></li>
                      <li>En formato, elige <strong>"GeoJSON"</strong></li>
                      <li>Guarda el archivo con extensión <code className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">.geojson</code></li>
                    </ol>
                  </div>
                  <div className="mt-3 flex items-center space-x-2">
                    <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <a
                      href="https://qgis.org/es/site/forusers/download.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium flex items-center"
                    >
                      Descargar QGIS
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </div>

                {/* Método 2: AutoCAD Map 3D */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Método 2: Desde AutoCAD Map 3D</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Abre tu archivo DWG en AutoCAD Map 3D</li>
                      <li>Ve a <strong>"Mapas" → "Exportar" → "Exportar a KML"</strong></li>
                      <li>Guarda como archivo KML</li>
                      <li>Luego usa QGIS o un convertidor online para convertir KML a GeoJSON</li>
                    </ol>
                  </div>
                  <div className="mt-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      <strong>Nota:</strong> Este método requiere AutoCAD Map 3D (versión de pago).
                      Si no lo tienes, usa el Método 1 con QGIS.
                    </p>
                  </div>
                </div>

                {/* Método 3: Convertidores Online */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Método 3: Convertidores Online</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 dark:text-white mb-2">CloudConvert</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Soporta conversión directa de DWG/DXF a GeoJSON
                      </p>
                      <a
                        href="https://cloudconvert.com/dwg-to-geojson"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm font-medium flex items-center"
                      >
                        Ir al convertidor
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Zamzar</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Conversión de DWG/DXF a GeoJSON en línea
                      </p>
                      <a
                        href="https://www.zamzar.com/convert/dwg-to-geojson/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm font-medium flex items-center"
                      >
                        Ir al convertidor
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Método 4: Exportar a Shapefile primero */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg">Método 4: DWG → Shapefile → GeoJSON</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      Si tienes problemas con la conversión directa, puedes hacerlo en dos pasos:
                    </p>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Convierte DWG/DXF a Shapefile usando QGIS o FME</li>
                      <li>Luego convierte el Shapefile a GeoJSON (más fácil y compatible)</li>
                    </ol>
                  </div>
                </div>

                {/* Tips importantes */}
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <h5 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Consejos Importantes:</h5>
                  <ul className="list-disc list-inside space-y-1 text-sm text-blue-800 dark:text-blue-300">
                    <li><strong>Sistema de coordenadas:</strong> Asegúrate de que tu archivo DWG tenga un sistema de coordenadas definido (WGS84 / EPSG:4326 es el estándar para GeoJSON)</li>
                    <li><strong>Capas:</strong> Si tu DWG tiene múltiples capas, puedes exportar solo las que necesitas</li>
                    <li><strong>Atributos:</strong> Los atributos de los objetos CAD se convertirán en propiedades del GeoJSON</li>
                    <li><strong>Polígonos cerrados:</strong> Asegúrate de que los polígonos estén cerrados correctamente en AutoCAD antes de exportar</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formatos Soportados */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Formatos que Puedes Convertir
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              'Shapefile (.shp)',
              'KML/KMZ',
              'GPX',
              'CSV con coordenadas',
              'Excel con coordenadas',
              'AutoCAD (DWG/DXF)',
              'GeoPackage (.gpkg)',
              'GML',
              'PostGIS'
            ].map((format, index) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center shadow">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{format}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Estructura GeoJSON */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Estructura de un GeoJSON
          </h2>
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-green-400 text-sm">
{`{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "geometry": {
        "type": "Polygon",
        "coordinates": [[
          [-77.0828, -12.0464],
          [-77.0820, -12.0464],
          [-77.0820, -12.0456],
          [-77.0828, -12.0456],
          [-77.0828, -12.0464]
        ]]
      },
      "properties": {
        "lot_number": "LOT-001",
        "price": 50000,
        "area": 200,
        "status": "DISPONIBLE"
      }
    }
  ]
}`}
            </pre>
          </div>
          <div className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong className="text-gray-900 dark:text-white">Tipos de geometría soportados:</strong> Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon</p>
            <p><strong className="text-gray-900 dark:text-white">Coordenadas:</strong> Deben estar en formato [longitud, latitud] (WGS84 / EPSG:4326)</p>
          </div>
        </div>

        {/* Link to Documentation */}
        <div className="bg-orange-50 dark:bg-orange-900/30 rounded-xl p-8 mb-12 border border-orange-200 dark:border-orange-700">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Documentación de Propiedades GeoJSON
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Una vez que tengas tu archivo GeoJSON, necesitas agregar las propiedades correctas para cada tipo de elemento (lotes, áreas, marcadores, líneas).
            </p>
            <a
              href="/documentacion-geojson"
              className="inline-flex items-center px-6 py-3 rounded-lg font-semibold text-white transition-colors"
              style={{ background: brandConfig.getPrimaryGradient() }}
            >
              <FileJson className="w-5 h-5 mr-2" />
              Ver Documentación de Propiedades
            </a>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-8 border border-blue-200 dark:border-blue-700">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              ¿Necesitas Ayuda?
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Si tienes problemas para convertir tus archivos o necesitas asistencia con el procesamiento,
              nuestro equipo está aquí para ayudarte.
            </p>
            <div className="flex items-center justify-center">
              <a
                href={`https://wa.me/${brandConfig.whatsappSupport}?text=${encodeURIComponent(`Hola Vengo de ${brandConfig.domain}, tengo mi proyecto XXXX y necesito ayuda para procesar mis archivos a GeoJSON`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center shadow-lg"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Necesito ayuda para procesar mis archivos
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConvertirGeoJSON;
